import { Button, Form } from 'antd';
import BaseModal from 'components/BaseModal'
import { MODAL_SIZE_MEDIUM } from 'constants/Layout';
import _ from 'lodash'
import React, { forwardRef, useState, useImperativeHandle, useRef, useCallback, useMemo } from 'react'
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import { FieldTypes, nameRegexValidator, urlRegexValidator, generateFormFields } from 'utils/formUtils';
import ModalUtil from 'utils/modalUtil';
import JiraMappingForm from '../jira/JiraMappingForm';
import message from 'components/CustomMessage';
import { duplicateNameValidator } from 'utils/validatorUtil';
import { hasPermission } from 'utils/authService';
import { PermissionSetMap } from 'constants/RBACConstants';
import { useActiveUserContext } from 'contexts/ActiveUserContext';
import ServiceNowOAuthPopup from './ServiceNowOAuthPopup';

const AUTHORIZE_TYPE = {
    BASIC: "BASIC",
    OAUTH: "OAUTH"
}

function ServiceNowLoginConfigForm({name, loginConfigList, noMappingForm, onUpdateConfigComponent, ...props}, ref){
    const isEdit = !_.isUndefined(props.config);
    const [form] = Form.useForm();
    const [auth, setAuth] = useState(() => ((props.config?.tokenType && props.config.tokenType === "REQUEST_TOKEN") ? AUTHORIZE_TYPE.OAUTH : AUTHORIZE_TYPE.BASIC));
    const [data, setData] = useState(() => (props.config ? { 
        ...props.config,
        version: props.config.version ?? "v2",
        loginType: auth,
    } : { loginType: auth, version: "V2" }));
    const { sessionData } = useActiveUserContext();
    const thisRef = useRef();

    useImperativeHandle(ref,() => ({
        ...thisRef
    }))

    const showOAuthPopup = useCallback(() => {
        const paramsData = {clientId: data.clientId, clientSecret: data.clientSecret, instanceUrl: data.url, ...(props.config ? { loginConfigId: props.config.id } : {})};
        ModalUtil.show({
          content: <ServiceNowOAuthPopup afterClose={onUpdateConfigComponent} data={paramsData} />,
          afterClose: (failed, loginId) => {
            // this is required on integrations page when a new config login is added
            const refreshIntegrations = true;
            if (!failed) {
                // on onboarding UI updateConfigComponent expects object 
                // which contains id i.e config login id
                onUpdateConfigComponent && onUpdateConfigComponent(noMappingForm ? {id: loginId} : refreshIntegrations);
                message.success(`Service Now OAuth Login Configuration created`);
                // on onbaording UI integration mapping form does not need to show
                if (!isEdit && !noMappingForm) {
                    ModalUtil.show({
                        content: (
                            <JiraMappingForm
                                onUpdateConfigComponent={onUpdateConfigComponent}
                                name={name}
                                loginConfigId={props.config?.id ?? loginId}
                            />
                        ),
                    });
                }
            }
            thisRef.current && thisRef.current.close();
          },
        });
    }, [name, props.config, onUpdateConfigComponent, data.clientId, data.clientSecret, data.url, isEdit, noMappingForm]);

    const fields = useMemo(() => (
        [
            {
                type: FieldTypes.RADIO,
                options: [
                    ...(isEdit && auth === AUTHORIZE_TYPE.OAUTH ? [] : [{ label: "Basic Auth", value: AUTHORIZE_TYPE.BASIC }]),
                        ...(!sessionData?.isCustomerDeployedInstance
                        ? [{ label: "OAuth", value: AUTHORIZE_TYPE.OAUTH }]
                        : []),
                    ],
                    props: {
                      onChange: (e) => setAuth(e.target.value),
                    },
                    name: "authSelection",
            },
        ...(auth === AUTHORIZE_TYPE.BASIC 
            ?
            [{
                name: "name",
                label: "Name",
                props: {
                    autoFocus: true,
                    onChange: (e) => setData(prevData => ({...prevData, name: e.target.value}))
                },
                colSpan: 12,
                rules: [
                    {
                        required: true,
                        message: "Name is required"
                    },
                    duplicateNameValidator({list: loginConfigList}),
                    nameRegexValidator
                ],
            }, {
                name: "userName",
                label: "User Name",
                placeholder: "User Name",
                colSpan: 12,
                required: true,
                props: {
                    onChange: (e) => setData(prevData => ({...prevData, userName: e.target.value}))
                }
            },
            {
                name: "apiToken",
                label: "Password",
                type: FieldTypes.PASSWORD,
                colSpan: 12,
                required: true,
                props: {
                    onChange: (e) => setData(prevData => ({...prevData, apiToken: e.target.value}))
                }
            }]
            :
            []
        ),
        {
            name: "url",
            label: "URL",
            colSpan: 12,
            required: true,
            rules: [urlRegexValidator()],
            props: {
                onChange: (e) => setData(prevData => ({...prevData, url: e.target.value}))
            }
        },
        ...(auth === AUTHORIZE_TYPE.OAUTH
            ? 
            [{
                name: "clientId",
                label: "Client Id",
                type: FieldTypes.PASSWORD,
                colSpan: 12,
                props: {
                    onChange: (e) => setData(prevData => ({...prevData, clientId: e.target.value}))
                },
                required: true,
            },
            {
                name: "clientSecret",
                label: "Client Secret",
                type: FieldTypes.PASSWORD,
                colSpan: 12,
                props: {
                    onChange: (e) => setData(prevData => ({...prevData, clientSecret: e.target.value}))
                },
                required: true
            },
                {
                  type: "Label",
                  render: () => (
                    <Button type="primary" onClick={showOAuthPopup} 
                    disabled={[data.clientId, data.clientSecret, data.url].some((val) => _.isEmpty(val))}
                    >
                      Login with OAuth
                    </Button>
                  ),
                },
        ]
            :
            []
        ),
        ...(auth === AUTHORIZE_TYPE.BASIC
            ? 
            [{
                name: "version",
                label: "API Version",
                colSpan: 12,
                disabled: true
            }]
            :[]
            
        )]   
    ), [auth, loginConfigList, sessionData?.isCustomerDeployedInstance, showOAuthPopup, data.clientId, data.clientSecret, data.url, isEdit]);

    const onOk = async () => {
        await form.validateFields();
        try{
            const toolName = name.toUpperCase().replace(' ', '_');
            let payload = form.getFieldsValue();
            if(auth === AUTHORIZE_TYPE.OAUTH){
                payload = {
                    ...payload,
                    version: "V2",
                    tokenType: "ACCESS_TOKEN"
                }
            }
            const response = await ToolsIntegrationsAPI[!isEdit ? "postTicketSystemToolLoginDetails" : "updateTicketSystemToolLoginDetails"](toolName, payload, props.config?.id).promise;

            message.success(`${name} Login Configuration created`)
            if(!isEdit && !noMappingForm){
                ModalUtil.show({
                    content: <JiraMappingForm onUpdateConfigComponent={onUpdateConfigComponent} name={name} loginConfigId={response.id} />
                })
            }
            onUpdateConfigComponent && onUpdateConfigComponent(response);
        }catch(err){
            console.log(err);
            if(!_.isEmpty(err.response?.data?.errors)){
                const {field, defaultMessage} = err.response.data.errors[0];
                form.setFields([
                    {
                      name: field,
                      errors: [defaultMessage],
                    },
                ]);   
            }
            return Promise.reject(err.response?.data?.message);
        }
    };

    const modalProps = {
        title: <>{isEdit ? "Edit" : "Add"} {name} Login Credentials </>,
        width: MODAL_SIZE_MEDIUM,
        disabled: !hasPermission(isEdit ? PermissionSetMap.EDIT_TOOL_CONFIG : PermissionSetMap.WRITE_TOOLS),
        ...(auth === AUTHORIZE_TYPE.OAUTH ? { footer: null } : {}),
        onOk: onOk
    }

    return (
        <BaseModal
            modalProps={modalProps}
            ref={thisRef}
            afterClose={props.afterClose}
        >  
            <Form
                initialValues={{...data, authSelection: auth}}
                form={form}
                layout="vertical"
            >
                {generateFormFields(fields)}
            </Form>
        </BaseModal>
    )
}

export default forwardRef(ServiceNowLoginConfigForm);
